import React, { useState } from "react";
import axios from "axios";
import { SERVER_URL } from "../config";
import { toast } from "react-toastify";
import "../styles/questionnaire.css";

interface Condition {
  field?: string;
  operator?: string;
  value?: string;
}

interface AnswerOption {
  code: string;
  key: string;
  text: string;
  next?: string;
}

interface Field {
  key: string; // e.g. "firstName"
  label: string; // e.g. "First Name"
  inputType: "text" | "date" | "phone";
}
interface Question {
  id: string; // The question code (e.g. "Q3" or "DISQUALIFY")
  type: string;
  header: string;
  description?: string;
  condition?: Condition;
  next?: string;
  answerOptions: AnswerOption[];
  fields?: Field[];
}

interface QuestionnaireData {
  title: string;
  description?: string;
  questions: Question[];
}

const CreateQuestionnaire: React.FC = () => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(false);

  // Add a new question with defaults
  const addQuestion = () => {
    const newCode = `Q${questions.length + 1}`.toUpperCase();
    const newQuestion: Question = {
      id: newCode,
      type: "text",
      header: "",
      description: "",
      answerOptions: [],
      fields: [],
    };
    setQuestions((prev) => [...prev, newQuestion]);
  };

  // Update a question at index with partial fields
  const updateQuestion = (index: number, updatedField: Partial<Question>) => {
    setQuestions((prev) => {
      const newQuestions = [...prev];
      newQuestions[index] = { ...newQuestions[index], ...updatedField };
      return newQuestions;
    });
  };

  // Ensure condition object exists if user clicks "Add/Edit Condition"
  const ensureCondition = (qIndex: number) => {
    const question = questions[qIndex];
    if (!question.condition) {
      updateQuestion(qIndex, {
        condition: { field: "", operator: "equals", value: "" },
      });
    }
  };

  // Add a new answer option to a specific question
  const addAnswerOption = (qIndex: number) => {
    setQuestions((prev) => {
      const updated = [...prev];
      const question = updated[qIndex];

      // Generate answer code automatically from question's code + letter
      const letterIndex = question.answerOptions.length; // 0 => 'A', 1 => 'B', etc.
      const letter = String.fromCharCode(65 + letterIndex); // 65='A'
      const baseCode = question.id.toUpperCase(); // ensure uppercase
      const newOption: AnswerOption = {
        code: baseCode + letter,
        key: "",
        text: "",
      };
      question.answerOptions.push(newOption);
      return updated;
    });
  };

  const addField = (qIndex: number) => {
    setQuestions((q) => {
      const qs = [...q];
      qs[qIndex].fields = qs[qIndex].fields || [];
      qs[qIndex].fields!.push({
        key: "",
        label: "",
        inputType: "text",
      });
      return qs;
    });
  };

  const updateField = (qIndex: number, fIndex: number, upd: Partial<Field>) => {
    setQuestions((q) => {
      const qs = [...q];
      const fs = qs[qIndex].fields || [];
      fs[fIndex] = { ...fs[fIndex], ...upd };
      qs[qIndex].fields = fs;
      return qs;
    });
  };

  const removeField = (qIndex: number, fIndex: number) => {
    setQuestions((q) => {
      const qs = [...q];
      qs[qIndex].fields = (qs[qIndex].fields || []).filter(
        (_, i) => i !== fIndex
      );
      return qs;
    });
  };

  // Update a specific answer option
  const updateAnswerOption = (
    qIndex: number,
    oIndex: number,
    updatedOption: Partial<AnswerOption>
  ) => {
    setQuestions((prev) => {
      const updated = [...prev];
      const ansOpts = updated[qIndex].answerOptions;
      const newOpts = [...ansOpts];
      // Force uppercase for code if user changes it
      if (updatedOption.code !== undefined) {
        updatedOption.code = updatedOption.code.toUpperCase();
      }
      newOpts[oIndex] = { ...newOpts[oIndex], ...updatedOption };
      updated[qIndex].answerOptions = newOpts;
      return updated;
    });
  };

  // Remove an answer option
  const removeAnswerOption = (qIndex: number, oIndex: number) => {
    setQuestions((prev) => {
      const updated = [...prev];
      updated[qIndex].answerOptions = updated[qIndex].answerOptions.filter(
        (_, idx) => idx !== oIndex
      );
      return updated;
    });
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    // The server expects title, description, and content fields.
    // So let's nest questions inside the content object.
    const questionnaire = {
      title,
      description,
      content: {
        questions, // put your question array here
      },
    };

    try {
      await axios.post(`${SERVER_URL}/api/questionnaire`, questionnaire, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      toast.success("Questionnaire created successfully!");

      // Reset form
      setTitle("");
      setDescription("");
      setQuestions([]);
    } catch (error: any) {
      console.error("Error creating questionnaire:", error);
      toast.error("Failed to create questionnaire.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create-questionnaire-container">
      <h2>Create Questionnaire</h2>
      <form onSubmit={handleSubmit} className="create-questionnaire-form">
        {/* TITLE & DESCRIPTION */}
        <div className="form-group">
          <label htmlFor="title">Title</label>
          <input
            type="text"
            id="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="description">Description (optional)</label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Enter a brief description..."
          />
        </div>

        {/* QUESTIONS */}
        <div className="questions-section">
          <h3>Questions</h3>
          {questions.map((question, qIndex) => (
            <div key={question.id} className="question-card">
              {/* QUESTION CODE */}
              <div className="form-group">
                <label>Question Code</label>
                <input
                  type="text"
                  value={question.id}
                  onChange={(e) =>
                    updateQuestion(qIndex, { id: e.target.value.toUpperCase() })
                  }
                  required
                />
                <small>Use uppercase (e.g. Q2, DISQUALIFY, etc.)</small>
              </div>

              {/* HEADER */}
              <div className="form-group">
                <label>Question Header</label>
                <input
                  type="text"
                  value={question.header}
                  onChange={(e) =>
                    updateQuestion(qIndex, { header: e.target.value })
                  }
                  required
                />
              </div>

              {/* TYPE */}
              <div className="form-group">
                <label>Question Type</label>
                <select
                  value={question.type}
                  onChange={(e) =>
                    updateQuestion(qIndex, { type: e.target.value })
                  }
                >
                  <option value="text">Text</option>
                  <option value="choice">Choice</option>
                  <option value="multiple-choice">Multiple Choice</option>
                  <option value="height-weight">Height-Weight</option>
                  <option value="number">Number</option>
                  <option value="disqualify">Disqualify</option>
                </select>
              </div>
              {question.type === "text" && (
                <div className="form-group">
                  <label>Fields</label>
                  {(question.fields || []).map((f, i) => (
                    <div key={i} className="field-row">
                      <select
                        value={f.inputType}
                        onChange={(e) =>
                          updateField(qIndex, i, {
                            inputType: e.target.value as any,
                          })
                        }
                      >
                        <option value="text">Text</option>
                        <option value="date">Date</option>
                        <option value="phone">Phone</option>
                      </select>
                      <input
                        type={f.inputType === "phone" ? "tel" : f.inputType}
                        placeholder={f.label || "Label"}
                        pattern={
                          f.inputType === "phone"
                            ? "\\(\\d{3}\\) \\d{3}-\\d{4}"
                            : undefined
                        }
                        onChange={(e) =>
                          updateField(qIndex, i, { label: e.target.value })
                        }
                      />
                      <button
                        type="button"
                        onClick={() => removeField(qIndex, i)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  <button type="button" onClick={() => addField(qIndex)}>
                    Add Field
                  </button>
                </div>
              )}

              {/* DESCRIPTION */}
              <div className="form-group">
                <label>Question Description (optional)</label>
                <textarea
                  value={question.description || ""}
                  onChange={(e) =>
                    updateQuestion(qIndex, { description: e.target.value })
                  }
                  placeholder="Optional description..."
                />
              </div>

              {/* CONDITION (optional) */}
              <div className="form-group">
                <label>Condition (optional)</label>
                <button
                  type="button"
                  onClick={() => ensureCondition(qIndex)}
                  className="condition-button"
                >
                  {question.condition ? "Edit Condition" : "Add Condition"}
                </button>
                {question.condition && (
                  <div className="condition-fields">
                    <input
                      type="text"
                      placeholder="field (e.g. gender)"
                      value={question.condition.field}
                      onChange={(e) =>
                        updateQuestion(qIndex, {
                          condition: {
                            ...question.condition,
                            field: e.target.value,
                          },
                        })
                      }
                    />
                    <select
                      value={question.condition.operator}
                      onChange={(e) =>
                        updateQuestion(qIndex, {
                          condition: {
                            ...question.condition,
                            operator: e.target.value,
                          },
                        })
                      }
                    >
                      <option value="equals">equals</option>
                      <option value="notEquals">notEquals</option>
                    </select>
                    <input
                      type="text"
                      placeholder="value (e.g. FEMALE)"
                      value={question.condition.value}
                      onChange={(e) =>
                        updateQuestion(qIndex, {
                          condition: {
                            ...question.condition,
                            value: e.target.value.toUpperCase(),
                          },
                        })
                      }
                    />
                  </div>
                )}
              </div>

              {/* DEFAULT NEXT QUESTION */}
              <div className="form-group">
                <label>Default Next Question (optional)</label>
                <input
                  type="text"
                  placeholder="e.g. Q2 or DISQUALIFY"
                  value={question.next || ""}
                  onChange={(e) =>
                    updateQuestion(qIndex, {
                      next: e.target.value.toUpperCase(),
                    })
                  }
                />
              </div>

              {/* TEXT FIELDS */}
              {question.type === "text" && (
                <div className="form-group">
                  <label>Text Fields</label>
                  {(question.fields || []).map((field, i) => (
                    <div key={i} className="field-row">
                      <input
                        type="text"
                        placeholder="Field key (e.g. firstName)"
                        value={field.key}
                        onChange={(e) =>
                          updateField(qIndex, i, { key: e.target.value })
                        }
                        required
                      />
                      <input
                        type="text"
                        placeholder="Label (e.g. First Name)"
                        value={field.label}
                        onChange={(e) =>
                          updateField(qIndex, i, { label: e.target.value })
                        }
                        required
                      />
                      <button
                        type="button"
                        onClick={() => removeField(qIndex, i)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  <button type="button" onClick={() => addField(qIndex)}>
                    Add Field
                  </button>
                </div>
              )}

              {/* ANSWER OPTIONS (if choice or multiple-choice) */}
              {(question.type === "choice" ||
                question.type === "multiple-choice") && (
                <div className="answer-options-section">
                  <h4>Answer Options</h4>
                  {question.answerOptions.map((option, oIndex) => (
                    <div key={oIndex} className="answer-option">
                      <input
                        type="text"
                        placeholder="Code"
                        value={option.code}
                        onChange={(e) =>
                          updateAnswerOption(qIndex, oIndex, {
                            code: e.target.value,
                          })
                        }
                        required
                      />
                      <input
                        type="text"
                        placeholder="Text"
                        value={option.text}
                        onChange={(e) =>
                          updateAnswerOption(qIndex, oIndex, {
                            text: e.target.value,
                          })
                        }
                        required
                      />
                      <input
                        type="text"
                        placeholder="Next Q ID (optional)"
                        value={option.next || ""}
                        onChange={(e) =>
                          updateAnswerOption(qIndex, oIndex, {
                            next: e.target.value,
                          })
                        }
                      />
                      <button
                        type="button"
                        onClick={() => removeAnswerOption(qIndex, oIndex)}
                        className="remove-option-button"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addAnswerOption(qIndex)}
                    className="add-option-button"
                  >
                    Add Answer Option
                  </button>
                </div>
              )}
              <hr />
            </div>
          ))}

          <button
            type="button"
            onClick={addQuestion}
            className="add-question-button"
          >
            Add Question
          </button>
        </div>

        {/* SUBMIT */}
        <button
          type="submit"
          disabled={loading}
          className="submit-questionnaire-button"
        >
          {loading ? "Creating Questionnaire..." : "Create Questionnaire"}
        </button>
      </form>
    </div>
  );
};

export default CreateQuestionnaire;
